import { useId } from "react";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { SessionStatus } from "@/lib/api/attendance";
import type { CourseUnit } from "@/lib/api/courseUnits";

type GuidanceFields = Pick<CourseUnit, "learning_objectives" | "preparation_guidance" | "recap" | "follow_up">;
export type GuidanceDraft = { preparation: string; recap: string; followUp: string };

// The recap and next steps reach learners only once the session is held
// (ADR 0030), so the box shows them only then.
const SHOWN: Array<{ label: string; value: (fields: GuidanceFields) => string; afterHeld?: true }> = [
  { label: "Session objective", value: fields => fields.learning_objectives.join("\n") },
  { label: "Preparation", value: fields => fields.preparation_guidance ?? "" },
  { label: "After-session recap", value: fields => fields.recap ?? "", afterHeld: true },
  { label: "Next steps", value: fields => fields.follow_up ?? "", afterHeld: true },
];
const DRAFT_FIELDS: Array<{ key: keyof GuidanceDraft; label: string; max: number; placeholder: string }> = [
  { key: "preparation", label: "Preparation", max: 4000, placeholder: "What students read, bring, or think about before session" },
  { key: "recap", label: "After-session recap", max: 2000, placeholder: "The takeaways, written after session" },
  { key: "followUp", label: "Next steps", max: 2000, placeholder: "What students do before the next session" },
];

/**
 * What students see now: the published revision, with its recap and next
 * steps only once the session is held. Where no line under Publish says the
 * plan's state (a reader, a closed term), it says plainly that nothing is
 * published; where one does, the box waits for a publication. Where the
 * editor's own sentence does not already say when the recap appears, the box
 * says it.
 */
function StudentsSee({ published, held, explainHeld, heading: Heading }: {
  published: GuidanceFields | null;
  held: boolean;
  explainHeld: boolean;
  heading: "h3" | "h4" | "h5" | "h6";
}) {
  const filled = published ? SHOWN.map(row => ({ ...row, value: row.value(published) })).filter(row => row.value.trim()) : [];
  const rows = held ? filled : filled.filter(row => !row.afterHeld);
  const withheld = rows.length < filled.length;
  return (
    <section aria-label="Students see now" className="grid content-start gap-2 rounded-control border border-border bg-card p-3">
      <Heading className="text-ui font-semibold text-foreground">Students see now</Heading>
      {rows.length === 0 ? (
        <p className="text-ui text-muted-foreground">{withheld ? "Nothing yet." : "Nothing published yet."}</p>
      ) : (
        <dl className="grid gap-2 text-ui">
          {rows.map(row => (
            <div key={row.label} className="grid gap-0.5">
              <dt className="text-muted-foreground">{row.label}</dt>
              <dd className="whitespace-pre-wrap text-foreground [overflow-wrap:anywhere]">{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
      {withheld && explainHeld && (
        <p className="text-meta text-muted-foreground">Published recap and next steps become visible when the session is recorded as held.</p>
      )}
      <p className="text-meta text-muted-foreground">The teaching outline and teaching notes stay private.</p>
    </section>
  );
}

/**
 * Student guidance: the one part of the session plan learners ever read, and
 * only once it is explicitly published (ADR 0030). The tile is the editor
 * (sprint decision Q5): Preparation, After-session recap and Next steps are
 * fields in place beside "Students see now", the published revision, and
 * Publish selects the acknowledged saved revision. Publish is filled where the
 * plan is the page's work, outlined where the page's filled act is elsewhere
 * (the session portal's time tile, ADR 0035). Unsaved edits are never
 * published; the one line under the act carries the card's save events and,
 * once they pass, the plan's state or Publish's reason, so "Students see now"
 * appears only once something is published. Without a draft (a reader, a
 * closed term) the same parts read as plain text.
 */
export function ClassPlanGuidance({
  idPrefix,
  saved,
  draft,
  onDraft,
  line,
  blocked,
  publishing,
  onPublish,
  publishedNotice,
  presentation = "tile",
  heading: Heading = "h3",
  publishEmphasis = "filled",
  sessionStatus = null,
}: {
  idPrefix: string;
  /** The acknowledged plan; null before the first save. */
  saved: CourseUnit | null;
  /** The status of the session the plan is on; null when it is on none. Only a held session releases the recap. */
  sessionStatus?: SessionStatus | null;
  /** Absent when the plan is read-only. */
  draft?: GuidanceDraft;
  onDraft?: (patch: Partial<GuidanceDraft>) => void;
  /** The line under Publish: a save event with its Retry, Publish's reason, or the plan's state. */
  line?: { label: string; retry?: () => void };
  /** Q5's rule: Publish waits. */
  blocked?: boolean;
  publishing?: boolean;
  onPublish?: () => void;
  publishedNotice?: boolean;
  presentation?: "tile" | "embedded";
  /** One level below the card's own heading. */
  heading?: "h3" | "h4" | "h5";
  /** Publish's weight: filled, or outline where the page's filled act is elsewhere. */
  publishEmphasis?: "filled" | "outline";
}) {
  const statusId = useId();
  const published = saved?.publication ?? null;
  const editable = !!draft && !!onDraft;
  // The line under Publish says "Not published" (or why Publish waits), so the box does not say it again.
  const showPublished = !!published || !(editable && line);
  const Sub = Heading === "h3" ? "h4" : Heading === "h4" ? "h5" : "h6";

  return (
    <div
      data-semantic="bento.planner.guidance"
      className={presentation === "tile"
        ? "-ms-5 -me-4 -mb-[1.125rem] grid gap-3 rounded-b-xl border-t border-rule bg-well ps-5 pe-4 py-3"
        : "grid gap-3 rounded-control bg-well px-3 py-2.5"}
    >
      <Heading className="flex min-h-[1.875rem] items-center text-ui font-semibold text-foreground">Student guidance</Heading>
      {publishedNotice && (
        <p role="status" className="text-ui text-foreground">Student guidance published. Students now see this update; the outline and notes stay private.</p>
      )}
      <div className={editable && showPublished ? "grid gap-3 md:grid-cols-2 md:gap-4" : "grid gap-3"}>
        {editable ? (
          <div className="grid content-start gap-3">
            {DRAFT_FIELDS.map(field => (
              <div key={field.key} className="grid gap-1.5">
                <Label htmlFor={`${idPrefix}-${field.key}`}>{field.label}</Label>
                <Textarea
                  id={`${idPrefix}-${field.key}`}
                  rows={2}
                  maxLength={field.max}
                  value={draft[field.key]}
                  placeholder={field.placeholder}
                  className="min-h-[var(--control-height)] resize-none"
                  onChange={event => onDraft({ [field.key]: event.target.value })}
                />
              </div>
            ))}
            <p className="text-meta text-muted-foreground">Students see the recap and next steps once the session is recorded as held.</p>
          </div>
        ) : saved && (
          <dl className="grid gap-2 text-ui">
            {DRAFT_FIELDS.map(field => {
              const value = { preparation: saved.preparation_guidance, recap: saved.recap, followUp: saved.follow_up }[field.key];
              return (
                <div key={field.key} className="grid gap-0.5">
                  <dt className="text-muted-foreground">{field.label}</dt>
                  <dd className="whitespace-pre-wrap text-foreground [overflow-wrap:anywhere]">{value || "Empty"}</dd>
                </div>
              );
            })}
          </dl>
        )}
        {showPublished && <StudentsSee published={published} held={sessionStatus === "held"} explainHeld={!editable} heading={Sub} />}
      </div>
      {editable && line && (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-rule pt-3">
          <p id={statusId} role="status" data-semantic="bento.planner.save" className="text-meta text-muted-foreground">{line.label}</p>
          {line.retry && <Button type="button" variant="link" size="sm" className="px-0" onClick={line.retry}>Retry</Button>}
          <Button
            data-semantic="bento.planner.publish"
            type="button"
            variant={publishEmphasis === "outline" ? "outline" : "default"}
            size="sm"
            className="ms-auto"
            aria-disabled={blocked || undefined}
            aria-describedby={statusId}
            onClick={() => { if (!blocked) onPublish?.(); }}
          >
            {publishing ? "Publishing" : "Publish"}
          </Button>
        </div>
      )}
    </div>
  );
}
